import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { format, startOfWeek, addDays, subWeeks, addWeeks } from "date-fns";
import { Receipt, ChevronLeft, ChevronRight, Save, Plus, History, Trash2, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const iso = (d) => format(d, "yyyy-MM-dd");

export default function PayPreview() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [data, setData] = useState({ rows: [], total_pay: 0, total_hours: 0 });
  const [rateEdits, setRateEdits] = useState({});
  const [payInput, setPayInput] = useState({});
  const [payDate, setPayDate] = useState({});
  const [loading, setLoading] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState([]);
  const weekEnd = addDays(weekStart, 6);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/staff/pay-preview", { params: { start: iso(weekStart), end: iso(weekEnd) } });
      setData(data);
      setRateEdits(Object.fromEntries(data.rows.filter((r) => r.user_id).map((r) => [r.user_id, r.hourly_rate])));
    } catch { toast.error("Failed to load pay preview."); }
    finally { setLoading(false); }
  }, [weekStart]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  async function saveRate(row) {
    try {
      await api.patch(`/staff/workers/${row.user_id}`, { hourly_rate: Number(rateEdits[row.user_id] || 0) });
      toast.success("Rate saved.");
      load();
    } catch { toast.error("Failed to save rate."); }
  }

  async function openHistory(row) {
    try {
      const { data } = await api.get("/staff/pay-payments", { params: { user_id: row.user_id, start: iso(weekStart), end: iso(weekEnd) } });
      setHistory(data); setHistoryFor({ user_id: row.user_id, name: row.worker_name || row.worker_email });
    } catch { toast.error("Failed to load history."); }
  }

  async function addPayment(row, amount, paidOn) {
    const amt = Number(amount);
    if (!amt) return toast.error("Enter a non-zero amount.");
    try {
      await api.post("/staff/pay-payment", { user_id: row.user_id, start: iso(weekStart), end: iso(weekEnd), amount: amt, paid_on: paidOn || iso(new Date()) });
      toast.success("Payment recorded.");
      setPayInput((s) => ({ ...s, [row.user_id]: "" }));
      await load();
      if (historyFor?.user_id === row.user_id) openHistory(row);
    } catch { toast.error("Failed to record payment."); }
  }

  function payInFull(row) {
    if (row.remainder <= 0.004) return toast.info("Already paid in full.");
    addPayment(row, Number(row.remainder.toFixed(2)), payDate[row.user_id] || iso(new Date()));
  }

  async function deletePayment(id) {
    try {
      await api.delete(`/staff/pay-payment/${id}`);
      toast.success("Payment removed.");
      await load();
      if (historyFor) openHistory({ user_id: historyFor.user_id, worker_name: historyFor.name });
    } catch { toast.error("Failed to remove payment."); }
  }

  return (
    <div>
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-primary"><Receipt className="h-6 w-6" /></div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Pay Preview</h1>
          <p className="text-sm text-muted-foreground">{isAdmin ? "Set hourly rates and preview pay = approved hours × rate." : "Your estimated pay based on admin-approved hours."}</p>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-card p-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="rounded-full" onClick={() => setWeekStart(subWeeks(weekStart, 1))} data-testid="pp-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <div className="min-w-[220px] text-center">
            <p className="font-heading font-bold" data-testid="pp-week">{format(weekStart, "MMM d")} – {format(weekEnd, "MMM d, yyyy")}</p>
            <p className="text-xs text-muted-foreground">Mon–Sun</p>
          </div>
          <Button variant="outline" size="icon" className="rounded-full" onClick={() => setWeekStart(addWeeks(weekStart, 1))} data-testid="pp-next"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="ghost" className="rounded-full" onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))} data-testid="pp-this-week">This week</Button>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">{isAdmin ? "Total pay (all workers)" : "Your estimated pay"}</p>
          <p className="font-heading text-2xl font-extrabold text-primary" data-testid="pp-total-pay">{money(data.total_pay)}</p>
        </div>
      </div>

      <div className="mt-6 overflow-hidden rounded-xl border border-border/60 bg-card">
        <Table>
          <TableHeader><TableRow>
            {isAdmin && <TableHead>Worker</TableHead>}
            <TableHead className="text-right">Approved Hours</TableHead>
            <TableHead className="text-right">Hourly Rate</TableHead>
            <TableHead className="text-right">Pay</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4} className="py-10 text-center text-muted-foreground">Loading…</TableCell></TableRow>
            ) : data.rows.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="py-10 text-center text-muted-foreground" data-testid="pp-empty">No approved hours for this week.</TableCell></TableRow>
            ) : data.rows.map((r, i) => (
              <TableRow key={i} data-testid={`pp-row-${i}`}>
                {isAdmin && <TableCell className="font-medium">{r.worker_name || r.worker_email}</TableCell>}
                <TableCell className="text-right">{r.hours.toFixed(2)}</TableCell>
                <TableCell className="text-right">
                  {isAdmin && r.user_id ? (
                    <div className="flex items-center justify-end gap-1">
                      <span className="text-sm text-muted-foreground">$</span>
                      <Input type="number" step="0.01" min="0" className="h-8 w-24 text-right" value={rateEdits[r.user_id] ?? 0}
                        onChange={(e) => setRateEdits((s) => ({ ...s, [r.user_id]: e.target.value }))} data-testid={`pp-rate-${i}`} />
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => saveRate(r)} data-testid={`pp-rate-save-${i}`}><Save className="h-3.5 w-3.5" /></Button>
                    </div>
                  ) : money(r.hourly_rate)}
                </TableCell>
                <TableCell className="text-right font-semibold" data-testid={`pp-pay-${i}`}>{money(r.pay)}</TableCell>
              </TableRow>
            ))}
            {data.rows.length > 0 && (
              <TableRow className="bg-secondary/40">
                {isAdmin && <TableCell className="font-heading font-bold">Total</TableCell>}
                <TableCell className="text-right font-bold">{data.total_hours.toFixed(2)}</TableCell>
                <TableCell />
                <TableCell className="text-right font-bold" data-testid="pp-total">{money(data.total_pay)}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {isAdmin && data.rows.length > 0 && (
        <div className="mt-8">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-heading text-xl font-bold">Payments &amp; Remainder</h2>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Outstanding this week</p>
              <p className="font-heading text-lg font-extrabold text-orange-600" data-testid="pp-outstanding">{money(data.total_remainder)}</p>
            </div>
          </div>
          <div className="overflow-x-auto rounded-xl border border-border/60 bg-card">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Worker</TableHead>
                <TableHead className="text-right">Pay Owed</TableHead>
                <TableHead className="text-right">Paid</TableHead>
                <TableHead className="text-right">Remainder</TableHead>
                <TableHead className="text-right">Record payment</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.rows.filter((r) => r.user_id).map((r, i) => (
                  <TableRow key={r.user_id} data-testid={`pp-paid-row-${i}`}>
                    <TableCell className="font-medium">{r.worker_name || r.worker_email}</TableCell>
                    <TableCell className="text-right">{money(r.pay)}</TableCell>
                    <TableCell className="text-right" data-testid={`pp-paid-${i}`}>{money(r.amount_paid)}</TableCell>
                    <TableCell className={`text-right font-semibold ${r.remainder > 0.004 ? "text-orange-600" : r.remainder < -0.004 ? "text-blue-600" : "text-primary"}`} data-testid={`pp-remainder-${i}`}>{money(r.remainder)}{r.remainder < -0.004 ? <span className="ml-1 text-[11px] font-normal text-muted-foreground">(overpaid)</span> : null}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Input type="date" className="h-8 w-[9.5rem]" value={payDate[r.user_id] ?? iso(new Date())}
                          onChange={(e) => setPayDate((s) => ({ ...s, [r.user_id]: e.target.value }))} data-testid={`pp-add-date-${i}`} />
                        <span className="text-sm text-muted-foreground">$</span>
                        <Input type="number" step="0.01" className="h-8 w-24 text-right" placeholder="0.00" value={payInput[r.user_id] ?? ""}
                          onChange={(e) => setPayInput((s) => ({ ...s, [r.user_id]: e.target.value }))} data-testid={`pp-add-amount-${i}`} />
                        <Button size="sm" className="h-8 rounded-full" onClick={() => addPayment(r, payInput[r.user_id], payDate[r.user_id])} data-testid={`pp-add-save-${i}`}><Plus className="mr-1 h-3.5 w-3.5" />Add</Button>
                        <Button size="sm" variant="outline" className="h-8 rounded-full" disabled={r.remainder <= 0.004} onClick={() => payInFull(r)} data-testid={`pp-paidfull-${i}`}><CheckCircle2 className="mr-1 h-3.5 w-3.5" />Paid in full</Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8" title="Payment history" onClick={() => openHistory(r)} data-testid={`pp-history-${i}`}><History className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-secondary/40">
                  <TableCell className="font-heading font-bold">Total</TableCell>
                  <TableCell className="text-right font-bold">{money(data.total_pay)}</TableCell>
                  <TableCell className="text-right font-bold" data-testid="pp-total-paid">{money(data.total_paid)}</TableCell>
                  <TableCell className="text-right font-bold" data-testid="pp-total-remainder">{money(data.total_remainder)}</TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <Dialog open={!!historyFor} onOpenChange={(o) => { if (!o) { setHistoryFor(null); setHistory([]); } }}>
        <DialogContent data-testid="pp-history-dialog">
          <DialogHeader>
            <DialogTitle>Payment history — {historyFor?.name}</DialogTitle>
            <DialogDescription>{format(weekStart, "MMM d")} – {format(weekEnd, "MMM d, yyyy")}</DialogDescription>
          </DialogHeader>
          {history.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground" data-testid="pp-history-empty">No payments recorded for this week yet.</p>
          ) : (
            <div className="space-y-2">
              {history.map((p) => (
                <div key={p.id} className="flex items-center justify-between rounded-lg border border-border/60 p-2.5" data-testid={`pp-history-item-${p.id}`}>
                  <div>
                    <p className="font-heading font-bold">{money(p.amount)}</p>
                    <p className="text-xs text-muted-foreground">Paid {p.paid_on ? format(new Date(p.paid_on + "T00:00:00"), "MMM d, yyyy") : format(new Date(p.created_at), "MMM d, yyyy")} · by {p.paid_by || "admin"}</p>
                  </div>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600 hover:text-red-600" onClick={() => deletePayment(p.id)} data-testid={`pp-history-del-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
              <div className="flex items-center justify-between border-t border-border/60 pt-2 font-bold">
                <span>Total paid</span><span data-testid="pp-history-total">{money(history.reduce((a, p) => a + p.amount, 0))}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
